import React, { useState, useRef, useEffect } from 'react';

/**
 * PLACE BET ENGINEER VIEW
 *
 * Complete visibility into EVERY step of placing a bet
 * Shows auth checks, API calls, state updates, database operations
 */

interface FlowEvent {
  id: string;
  timestamp: number;
  type: 'check' | 'api' | 'state' | 'db' | 'error' | 'success';
  step: string;
  details: any;
  duration?: number;
}

interface BetData {
  marketId: string;
  marketQuestion: string;
  optionId: string;
  optionText: string;
  amount: number;
  userId?: string;
  userEmail?: string;
  balance?: number;
}

export default function PlaceBetEngineer() {
  // Bet configuration
  const [betData, setBetData] = useState<BetData>({
    marketId: 'market-claude-oct-2025',
    marketQuestion: 'Will Claude Code become Brian\'s most-used AI tool by October 2025?',
    optionId: 'option-yes',
    optionText: 'Claude Code #1',
    amount: 100
  });

  // Flow state
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [events, setEvents] = useState<FlowEvent[]>([]);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [redirecting, setRedirecting] = useState(false);

  // Refs
  const startTimeRef = useRef<number>(0);
  const eventsEndRef = useRef<HTMLDivElement>(null);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const logEvent = (type: FlowEvent['type'], step: string, details: any) => {
    const now = Date.now();
    const duration = startTimeRef.current ? now - startTimeRef.current : 0;

    const event: FlowEvent = {
      id: `evt_${now}_${Math.random()}`,
      timestamp: now,
      type,
      step,
      details,
      duration
    };

    setEvents(prev => [...prev, event]);
    setCurrentStep(step);
  };

  const checkAuth = async () => {
    logEvent('check', 'AUTH_CHECK', { checking: 'localStorage for session' });

    const session = localStorage.getItem('session');
    if (!session) {
      logEvent('error', 'AUTH_FAILED', {
        reason: 'No session found',
        action: 'Saving bet intent and redirecting to delightful login'
      });

      // Save the user's betting intent before redirect
      const betIntent = {
        marketId: betData.marketId,
        marketQuestion: betData.marketQuestion,
        optionId: betData.optionId,
        optionText: betData.optionText,
        amount: betData.amount,
        returnTo: '/bet-engineer',
        timestamp: Date.now()
      };

      localStorage.setItem('betIntent', JSON.stringify(betIntent));

      logEvent('state', 'BET_INTENT_SAVED', {
        message: '🎯 We saved your bet! Login and we\'ll place it automatically.',
        intent: betIntent
      });

      // Show auth flow preview, then redirect
      await showDelightfulAuthPreview();

      // Actually redirect to login
      setTimeout(() => {
        window.location.href = '/otp-login?returnTo=/bet-engineer&intent=bet';
      }, 2000);

      throw new Error('Redirecting to magical login experience...');
    }

    const sessionData = JSON.parse(session);

    // Check session expiry
    if (Date.now() > sessionData.expiresAt) {
      logEvent('error', 'SESSION_EXPIRED', {
        expired: new Date(sessionData.expiresAt).toISOString(),
        action: 'Would clear session and redirect'
      });
      throw new Error('Session expired');
    }

    logEvent('check', 'AUTH_SUCCESS', {
      userId: sessionData.userId,
      email: sessionData.email,
      balance: sessionData.balance,
      expiresIn: Math.floor((sessionData.expiresAt - Date.now()) / 1000 / 60) + ' minutes'
    });

    // Update bet data with user info
    setBetData(prev => ({
      ...prev,
      userId: sessionData.userId,
      userEmail: sessionData.email,
      balance: sessionData.balance
    }));

    return sessionData;
  };

  const showDelightfulAuthPreview = async () => {
    logEvent('success', 'DELIGHTFUL_REDIRECT', {
      title: '🎉 Let\'s Get You Logged In!',
      message: 'We\'re saving your bet and taking you to our magical login',
      features: [
        '✨ No passwords to remember',
        '📧 Just your email',
        '🔢 6-digit code sent instantly',
        '🎯 Your bet will be waiting'
      ]
    });
    await new Promise(r => setTimeout(r, 1000));

    logEvent('state', 'TRANSITION_STARTING', {
      effect: 'Smooth fade transition',
      destination: '/otp-login',
      preserved: 'Your betting intent',
      duration: '2 seconds'
    });
  };

  const showAuthFlow = async () => {
    // Show complete authentication flow visualization
    logEvent('state', 'AUTH_FLOW_START', {
      step: '1. REDIRECT',
      description: 'User redirected to /otp-login',
      url: '/otp-login'
    });
    await new Promise(r => setTimeout(r, 500));

    logEvent('state', 'EMAIL_ENTRY', {
      step: '2. EMAIL INPUT',
      description: 'User enters email address',
      validation: 'Email format check',
      example: 'user@example.com'
    });
    await new Promise(r => setTimeout(r, 500));

    logEvent('api', 'SEND_OTP', {
      step: '3. GENERATE & SEND OTP',
      endpoint: '/api/send-email',
      payload: {
        email: 'user@example.com',
        code: '******'
      },
      email_provider: 'Resend',
      domain: 'updates.futr.bet'
    });
    await new Promise(r => setTimeout(r, 500));

    logEvent('db', 'STORE_OTP', {
      step: '4. STORE OTP IN DATABASE',
      table: 'otp_codes',
      operation: 'INSERT',
      fields: ['email', 'code', 'expires_at'],
      ttl: '10 minutes'
    });
    await new Promise(r => setTimeout(r, 500));

    logEvent('state', 'EMAIL_SENT', {
      step: '5. EMAIL DELIVERED',
      status: 'Email sent to inbox',
      contains: '6-digit code',
      provider_id: 'msg_xxxxx'
    });
    await new Promise(r => setTimeout(r, 500));

    logEvent('state', 'CODE_ENTRY', {
      step: '6. USER ENTERS CODE',
      interface: '6 input boxes',
      validation: 'Numeric only',
      auto_submit: 'When 6 digits entered'
    });
    await new Promise(r => setTimeout(r, 500));

    logEvent('api', 'VERIFY_OTP', {
      step: '7. VERIFY CODE',
      endpoint: '/api/otp/verify',
      checks: [
        'Code exists in DB',
        'Code not expired',
        'Code not already used',
        'Timing-safe comparison'
      ]
    });
    await new Promise(r => setTimeout(r, 500));

    logEvent('db', 'USER_LOOKUP', {
      step: '8. USER ACCOUNT',
      operation: 'SELECT or CREATE',
      table: 'users',
      result: 'User record found/created',
      initial_balance: 1000
    });
    await new Promise(r => setTimeout(r, 500));

    logEvent('state', 'GENERATE_SESSION', {
      step: '9. CREATE SESSION',
      token_type: 'JWT',
      contains: ['userId', 'email', 'iat', 'exp'],
      storage: 'HttpOnly Cookie + localStorage',
      duration: '24 hours'
    });
    await new Promise(r => setTimeout(r, 500));

    logEvent('success', 'AUTH_COMPLETE', {
      step: '10. AUTHENTICATION SUCCESSFUL',
      result: 'User logged in',
      next: 'Return to betting flow',
      session_created: true
    });
  };

  const checkMarketExists = async () => {
    logEvent('check', 'MARKET_CHECK', {
      marketId: betData.marketId,
      action: 'Checking if market exists in database'
    });

    // For demo, we know this market doesn't exist yet
    const marketExists = false; // In real app, would call API to check

    if (!marketExists) {
      logEvent('state', 'MARKET_MISSING', {
        marketId: betData.marketId,
        action: 'Need to create market first',
        redirecting: true
      });

      logEvent('state', 'REDIRECT_TO_CREATE', {
        destination: '/',
        reason: 'Creating market: ' + betData.marketQuestion
      });

      // This isn't an error - it's a normal redirect!
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);

      return false;
    }

    logEvent('check', 'MARKET_EXISTS', {
      marketId: betData.marketId,
      status: 'active'
    });

    return true;
  };

  const validateBet = async (session: any) => {
    logEvent('check', 'VALIDATE_BET', {
      amount: betData.amount,
      balance: session.balance
    });

    // Check amount
    if (betData.amount <= 0) {
      logEvent('error', 'INVALID_AMOUNT', { amount: betData.amount });
      throw new Error('Amount must be greater than 0');
    }

    if (betData.amount > (session.balance || 1000)) {
      logEvent('error', 'INSUFFICIENT_BALANCE', {
        required: betData.amount,
        available: session.balance
      });
      throw new Error('Insufficient balance');
    }

    // Check option exists
    logEvent('check', 'OPTION_CHECK', {
      optionId: betData.optionId,
      validOptions: ['option-yes', 'option-no']
    });

    logEvent('check', 'VALIDATION_SUCCESS', {
      bet: betData,
      checks: ['amount_valid', 'balance_sufficient', 'option_valid']
    });
  };

  const prepareBetPayload = (session: any) => {
    const payload = {
      user_id: session.userId,
      market_id: betData.marketId,
      option_id: betData.optionId,
      amount: betData.amount,
      shares: betData.amount // Simplified 1:1
    };

    logEvent('state', 'PREPARE_PAYLOAD', {
      payload,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer [token_would_be_here]'
      }
    });

    return payload;
  };

  const callBetAPI = async (payload: any) => {
    logEvent('api', 'API_CALL_START', {
      endpoint: 'https://futr-bet-api.b-9f2.workers.dev/api/bets',
      method: 'POST',
      payload
    });

    try {
      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/api/bets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      logEvent('api', 'API_RESPONSE', {
        status: response.status,
        ok: response.ok,
        data,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        // Handle specific error codes
        switch (data.code) {
          case 'USER_NOT_FOUND':
            logEvent('error', 'USER_NOT_FOUND', {
              message: 'User session invalid',
              action: 'Need to re-login'
            });
            localStorage.removeItem('session');
            setTimeout(() => window.location.href = '/otp-login', 2000);
            throw new Error('Session expired! Redirecting to login...');

          case 'MARKET_NOT_FOUND':
            logEvent('error', 'MARKET_NOT_FOUND', {
              message: data.details,
              action: 'Redirecting to create market',
              marketId: payload.market_id
            });

            // Set error message first
            const errorMsg = `Market not found! Redirecting to create it...`;

            // Mark that we're redirecting
            setRedirecting(true);

            // Schedule redirect using ref to persist it
            console.log('[DEBUG] Scheduling redirect to / in 2 seconds');
            logEvent('state', 'REDIRECT_SCHEDULED', {
              destination: '/',
              delay: '2 seconds'
            });

            // Store timeout in ref so it persists
            redirectTimeoutRef.current = setTimeout(() => {
              console.log('[DEBUG] Redirecting NOW to /');
              window.location.replace('/');
            }, 2000);

            throw new Error(errorMsg);

          case 'INSUFFICIENT_BALANCE':
            logEvent('error', 'INSUFFICIENT_BALANCE', {
              needed: payload.amount,
              available: data.details.match(/\d+/g)?.[1] || 0
            });
            throw new Error(data.details || 'Not enough points!');

          case 'MARKET_NOT_ACTIVE':
            logEvent('error', 'MARKET_CLOSED', {
              status: data.details
            });
            throw new Error(data.error);

          case 'INVALID_OPTION':
            logEvent('error', 'INVALID_OPTION', {
              option: payload.option_id,
              market: payload.market_id
            });
            throw new Error(data.error);

          default:
            throw new Error(data.error || 'API call failed');
        }
      }

      setApiResponse(data);
      return data;
    } catch (error) {
      logEvent('error', 'API_ERROR', {
        error: error.message,
        type: error.name
      });
      throw error;
    }
  };

  const updateLocalState = (betResponse: any, session: any) => {
    logEvent('state', 'UPDATE_LOCAL_STATE', {
      updating: ['user_balance', 'user_bets', 'localStorage']
    });

    // Use the balance from the API response if available, otherwise calculate
    const newBalance = betResponse.user?.balance ?? ((session.balance || 1000) - betData.amount);
    const updatedSession = {
      ...session,
      balance: newBalance
    };

    localStorage.setItem('session', JSON.stringify(updatedSession));

    logEvent('state', 'BALANCE_UPDATED', {
      old: session.balance,
      new: newBalance,
      change: -betData.amount,
      source: betResponse.user?.balance ? 'API Response' : 'Client Calculation'
    });

    // Store bet in local history
    const betHistory = JSON.parse(localStorage.getItem('userBets') || '[]');
    const newBet = {
      ...betResponse.data,
      marketQuestion: betData.marketQuestion,
      optionText: betData.optionText,
      timestamp: new Date().toISOString()
    };

    betHistory.push(newBet);
    localStorage.setItem('userBets', JSON.stringify(betHistory));

    logEvent('state', 'BET_STORED', {
      betId: betResponse.data.id,
      totalBets: betHistory.length
    });

    return newBalance;
  };

  const simulateDBOperations = () => {
    logEvent('db', 'DB_OPERATIONS', {
      operations: [
        {
          table: 'bets',
          action: 'INSERT',
          sql: 'INSERT INTO bets (id, user_id, market_id, option_id, amount, shares, price_per_share) VALUES (?, ?, ?, ?, ?, ?, ?)'
        },
        {
          table: 'users',
          action: 'UPDATE',
          sql: 'UPDATE users SET balance = balance - ? WHERE id = ?'
        },
        {
          table: 'markets',
          action: 'UPDATE',
          sql: 'UPDATE markets SET total_pool = total_pool + ?, participant_count = participant_count + 1 WHERE id = ?'
        },
        {
          table: 'market_options',
          action: 'UPDATE',
          sql: 'UPDATE market_options SET total_bets = total_bets + ?, bet_count = bet_count + 1 WHERE id = ?'
        }
      ]
    });
  };

  // Check for returning user with bet intent
  useEffect(() => {
    const checkBetIntent = async () => {
      const intentStr = localStorage.getItem('betIntent');
      if (!intentStr) return;

      const intent = JSON.parse(intentStr);
      const session = localStorage.getItem('session');

      // If we have both intent and session, auto-place the bet!
      if (session && intent.timestamp > Date.now() - 300000) { // 5 min expiry
        setBetData({
          marketId: intent.marketId,
          marketQuestion: intent.marketQuestion,
          optionId: intent.optionId,
          optionText: intent.optionText,
          amount: intent.amount
        });

        // Clear the intent
        localStorage.removeItem('betIntent');

        // Auto-place after a moment
        setTimeout(() => {
          logEvent('success', '🎉 WELCOME BACK!', {
            message: 'Placing your saved bet now!',
            bet: intent
          });
          placeBet();
        }, 1500);
      }
    };

    checkBetIntent();
  }, []);

  const placeBet = async () => {
    setIsRunning(true);
    setError('');
    setEvents([]);
    setApiResponse(null);
    startTimeRef.current = Date.now();

    try {
      logEvent('check', 'BUTTON_CLICKED', {
        action: 'Place Bet Button',
        reality: 'Smart flow: Auth → Market → Bet',
        timestamp: new Date().toISOString()
      });
      await new Promise(r => setTimeout(r, 200));

      logEvent('check', 'SMART_FLOW', {
        fact: 'Intelligent checks before attempting bet',
        flow: '1. Check Auth → 2. Check Market Exists → 3. Place Bet',
        philosophy: 'Fail fast, fail gracefully'
      });
      await new Promise(r => setTimeout(r, 200));

      // Step 1: Auth check
      const session = await checkAuth();
      await new Promise(r => setTimeout(r, 300));

      // Step 2: Check if market exists (BEFORE trying to bet!)
      const marketExists = await checkMarketExists();
      if (!marketExists) {
        setError('Market doesn\'t exist yet! Redirecting to create it...');
        return; // Stop here - no error, just redirect
      }
      await new Promise(r => setTimeout(r, 300));

      // Step 3: Validate bet details
      await validateBet(session);
      await new Promise(r => setTimeout(r, 300));

      // Step 3: Prepare payload
      const payload = prepareBetPayload(session);
      await new Promise(r => setTimeout(r, 300));

      // Step 4: Call API
      const betResponse = await callBetAPI(payload);
      await new Promise(r => setTimeout(r, 300));

      // Step 5: Show DB operations
      simulateDBOperations();
      await new Promise(r => setTimeout(r, 300));

      // Step 6: Update local state
      const newBalance = updateLocalState(betResponse, session);
      await new Promise(r => setTimeout(r, 300));

      // Success!
      logEvent('success', 'BET_PLACED', {
        betId: betResponse.data.id,
        amount: betData.amount,
        option: betData.optionText,
        newBalance,
        totalDuration: Date.now() - startTimeRef.current + 'ms'
      });

    } catch (err) {
      setError(err.message);
      logEvent('error', 'FLOW_FAILED', {
        error: err.message,
        step: currentStep,
        duration: Date.now() - startTimeRef.current + 'ms'
      });

      // If error is about market not found, ensure redirect happens
      if (err.message.includes('Market not found')) {
        console.log('[DEBUG] Error caught, ensuring redirect happens');
        if (!redirecting) {
          setRedirecting(true);
          setTimeout(() => {
            console.log('[DEBUG] Fallback redirect executing');
            window.location.replace('/');
          }, 2000);
        }
      }
    } finally {
      setIsRunning(false);
    }
  };

  const resetFlow = () => {
    setEvents([]);
    setError('');
    setApiResponse(null);
    setCurrentStep('');
    startTimeRef.current = 0;
    setRedirecting(false);
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }
  };

  const getStepIcon = (type: FlowEvent['type']) => {
    switch (type) {
      case 'check': return '🔍';
      case 'api': return '📡';
      case 'state': return '💾';
      case 'db': return '🗄️';
      case 'error': return '❌';
      case 'success': return '✅';
      default: return '⚡';
    }
  };

  const getStepColor = (type: FlowEvent['type']) => {
    switch (type) {
      case 'check': return 'text-blue-400';
      case 'api': return 'text-purple-400';
      case 'state': return 'text-yellow-400';
      case 'db': return 'text-cyan-400';
      case 'error': return 'text-red-400';
      case 'success': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 border border-green-400 p-4">
          <h1 className="text-3xl mb-2">🔧 PLACE BET - ENGINEER VIEW</h1>
          <p className="text-gray-400">See EXACTLY what happens when you click "Place Bet"</p>
          <p className="text-yellow-400 text-sm mt-2">
            💡 Truth: Every "Place Bet" button is actually a "Check Auth → Then Place Bet" button
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {/* Left: Bet Configuration */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4 text-yellow-400">BET CONFIGURATION</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">MARKET</label>
                <div className="text-sm">{betData.marketQuestion}</div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">OPTION</label>
                <select
                  value={betData.optionId}
                  onChange={(e) => setBetData({
                    ...betData,
                    optionId: e.target.value,
                    optionText: e.target.value === 'option-yes' ? 'Claude Code #1' : 'Another tool wins'
                  })}
                  className="w-full bg-gray-900 border border-gray-600 p-2 text-green-400"
                  disabled={isRunning}
                >
                  <option value="option-yes">Claude Code #1 (65%)</option>
                  <option value="option-no">Another tool wins (35%)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">AMOUNT</label>
                <input
                  type="number"
                  value={betData.amount}
                  onChange={(e) => setBetData({ ...betData, amount: parseInt(e.target.value) || 0 })}
                  className="w-full bg-gray-900 border border-gray-600 p-2 text-green-400"
                  disabled={isRunning}
                />
              </div>

              <button
                onClick={placeBet}
                disabled={isRunning}
                className="w-full py-3 bg-green-400 text-black font-bold hover:bg-green-300 disabled:opacity-50"
              >
                {isRunning ? '⏳ RUNNING CHECKS...' : '✅ CHECK ALL → PLACE BET'}
              </button>

              <button
                onClick={resetFlow}
                className="w-full py-2 bg-gray-700 text-white hover:bg-gray-600"
              >
                🔄 RESET
              </button>

              {error && (
                <div className={`p-3 border ${
                  error.includes('doesn\'t exist yet')
                    ? 'border-yellow-400 bg-yellow-900 animate-pulse'
                    : error.includes('Session expired')
                    ? 'border-green-400 bg-green-900 animate-pulse'
                    : 'border-red-400 bg-red-900'
                }`}>
                  <div className={`font-bold ${
                    error.includes('doesn\'t exist yet') ? 'text-yellow-400' :
                    error.includes('Session expired') ? 'text-green-400' :
                    'text-red-400'
                  }`}>
                    {error.includes('doesn\'t exist yet') ? '🎲 CREATING MARKET' :
                     error.includes('Session expired') ? '✨ SESSION EXPIRED' :
                     'ERROR'}
                  </div>
                  <div className="text-sm mt-1">{error}</div>
                  {error.includes('doesn\'t exist yet') && (
                    <div className="mt-3">
                      <div className="text-green-300 animate-pulse font-bold">
                        ✨ Taking you to create this market...
                      </div>
                      <button
                        onClick={() => window.location.href = '/'}
                        className="mt-2 w-full py-2 bg-yellow-500 text-black font-bold hover:bg-yellow-400"
                      >
                        → Create Market Now
                      </button>
                    </div>
                  )}
                  {error.includes('Authentication') && !error.includes('Redirecting') && (
                    <a
                      href="/otp-login?returnTo=/bet-engineer"
                      className="block mt-3 py-2 bg-green-400 text-black text-center font-bold hover:bg-green-300 transition-all hover:scale-105"
                    >
                      → GO TO MAGICAL LOGIN
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Current State */}
            <div className="mt-6 pt-4 border-t border-gray-600">
              <h3 className="text-sm text-yellow-400 mb-2">CURRENT STATE</h3>
              <pre className="text-xs text-gray-500">
{JSON.stringify({
  authenticated: !!localStorage.getItem('session'),
  userId: betData.userId || 'not_set',
  balance: betData.balance || 'unknown'
}, null, 2)}
              </pre>
            </div>

            {/* Universal Pattern */}
            <div className="mt-6 pt-4 border-t border-gray-600">
              <h3 className="text-sm text-yellow-400 mb-2">UNIVERSAL PATTERN</h3>
              <div className="text-xs text-gray-400 space-y-1">
                <div>Every protected button:</div>
                <div className="text-green-400 ml-2">1. Check Auth</div>
                <div className="text-gray-500 ml-2">2. If ✓ → Do Action</div>
                <div className="text-gray-500 ml-2">3. If ✗ → Login Flow</div>
              </div>
            </div>
          </div>

          {/* Middle: Event Flow */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4 text-yellow-400">EVENT FLOW</h2>

            <div className="h-[600px] overflow-y-auto space-y-2">
              {events.length === 0 ? (
                <div className="text-gray-600 text-center py-8">
                  Click "PLACE BET" to start...
                </div>
              ) : (
                events.map(event => (
                  <div
                    key={event.id}
                    className={`border-l-2 pl-3 ${
                      event.type === 'error' ? 'border-red-400' :
                      event.type === 'success' ? 'border-green-400' :
                      currentStep === event.step ? 'border-yellow-400' :
                      'border-gray-600'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{getStepIcon(event.type)}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`font-bold ${getStepColor(event.type)}`}>
                            {event.step}
                          </span>
                          {event.duration > 0 && (
                            <span className="text-xs text-gray-500">
                              +{event.duration}ms
                            </span>
                          )}
                        </div>
                        <pre className="text-xs text-gray-400 mt-1 overflow-x-auto">
{JSON.stringify(event.details, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={eventsEndRef} />
            </div>

            {/* Timeline */}
            {events.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-600">
                <div className="text-xs text-gray-500">
                  Total Duration: {events[events.length - 1]?.duration || 0}ms
                </div>
              </div>
            )}
          </div>

          {/* Right: API Response & Database */}
          <div className="space-y-4">
            {/* API Response */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-yellow-400">API RESPONSE</h2>

              {apiResponse ? (
                <pre className="text-xs text-gray-400 overflow-x-auto">
{JSON.stringify(apiResponse, null, 2)}
                </pre>
              ) : (
                <div className="text-gray-600 text-center py-8 text-sm">
                  No API response yet...
                </div>
              )}
            </div>

            {/* Database Operations */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-yellow-400">DATABASE OPS</h2>

              <div className="space-y-3 text-xs">
                <div className="p-2 bg-gray-900 border border-gray-700">
                  <div className="text-cyan-400 font-bold mb-1">D1 Database</div>
                  <div className="text-gray-500">CloudFlare Workers D1</div>
                </div>

                <div className="space-y-2">
                  <div className="text-gray-400">Tables affected:</div>
                  <ul className="space-y-1 text-gray-500">
                    <li>• bets (INSERT)</li>
                    <li>• users (UPDATE balance)</li>
                    <li>• markets (UPDATE pool)</li>
                    <li>• market_options (UPDATE totals)</li>
                  </ul>
                </div>

                <div className="p-2 bg-gray-900 border border-gray-700">
                  <div className="text-gray-400">Transaction:</div>
                  <div className="text-gray-500 mt-1">ATOMIC - all or nothing</div>
                </div>
              </div>
            </div>

            {/* Flow Diagram */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-yellow-400">FLOW STEPS</h2>

              {/* Check if showing auth flow */}
              {error?.includes('Authentication') ? (
                <div className="space-y-2 text-xs">
                  <div className="text-red-400 font-bold mb-2">🔐 AUTH FLOW REQUIRED</div>
                  <div className={currentStep.includes('REDIRECT') ? 'text-yellow-400' : 'text-gray-500'}>
                    1️⃣ Redirect to Login
                  </div>
                  <div className={currentStep.includes('EMAIL') ? 'text-yellow-400' : 'text-gray-500'}>
                    2️⃣ Enter Email
                  </div>
                  <div className={currentStep.includes('SEND_OTP') ? 'text-yellow-400' : 'text-gray-500'}>
                    3️⃣ Send OTP Code
                  </div>
                  <div className={currentStep.includes('STORE_OTP') ? 'text-yellow-400' : 'text-gray-500'}>
                    4️⃣ Store in Database
                  </div>
                  <div className={currentStep.includes('CODE_ENTRY') ? 'text-yellow-400' : 'text-gray-500'}>
                    5️⃣ Enter Code
                  </div>
                  <div className={currentStep.includes('VERIFY') ? 'text-yellow-400' : 'text-gray-500'}>
                    6️⃣ Verify Code
                  </div>
                  <div className={currentStep.includes('SESSION') ? 'text-yellow-400' : 'text-gray-500'}>
                    7️⃣ Create Session
                  </div>
                  <div className={currentStep.includes('AUTH_COMPLETE') ? 'text-green-400' : 'text-gray-500'}>
                    ✅ Authenticated!
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-xs">
                  <div className="text-green-400 font-bold mb-2">🎲 BET PLACEMENT FLOW</div>
                  <div className={currentStep.includes('BUTTON') || currentStep.includes('AUTH') ? 'text-yellow-400' : 'text-gray-500'}>
                    0️⃣ Button Click → AUTH CHECK
                  </div>
                  <div className={currentStep === 'AUTH_CHECK' || currentStep === 'AUTH_SUCCESS' ? 'text-green-400' : 'text-gray-500'}>
                    1️⃣ Auth Verified ✓
                  </div>
                  <div className={currentStep === 'VALIDATE_BET' ? 'text-yellow-400' : 'text-gray-500'}>
                    2️⃣ Validate Bet
                  </div>
                  <div className={currentStep === 'PREPARE_PAYLOAD' ? 'text-yellow-400' : 'text-gray-500'}>
                    3️⃣ Prepare Payload
                  </div>
                  <div className={currentStep === 'API_CALL_START' ? 'text-yellow-400' : 'text-gray-500'}>
                    4️⃣ Call API
                  </div>
                  <div className={currentStep === 'DB_OPERATIONS' ? 'text-yellow-400' : 'text-gray-500'}>
                    5️⃣ Database Ops
                  </div>
                  <div className={currentStep === 'UPDATE_LOCAL_STATE' ? 'text-yellow-400' : 'text-gray-500'}>
                    6️⃣ Update State
                  </div>
                  <div className={currentStep === 'BET_PLACED' ? 'text-green-400' : 'text-gray-500'}>
                    ✅ Bet Placed!
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}